import React, { useEffect, useState } from 'react';
import DataTable from 'react-data-table-component';
import customPagination from '../../../components/CustomPagination/CustomPagination';
import { BackButton, SkeletonTable } from '../../../components';
import { useLocation } from 'react-router-dom';
import { useStateContext } from '../../../contexts/ContextProvider';
import { userRequest } from '../../../utils/requestMethods';
import * as Icons from "../../../data/teacher";
import { MdWbSunny, MdCloud } from 'react-icons/md';

const customStyles = {
    headRow: {
        style: {
            backgroundColor: 'rgb(248, 250, 252)',
            fontWeight: "bold",
        },
    },
};

const ViewStudentAttendance = ({ history }) => {
    const location = useLocation();
    const studentId = location.pathname.split('/')[4];
    const student = location.state?.student;
    const classId = student?.class_info?._id;
    const { user, school } = useStateContext();
    const schoolTerm = school?.settings?.current_term;
    const schoolSession = school?.settings?.current_session;
    const [studentAttendance, setStudentAttendance] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedPeriod, setSelectedPeriod] = useState('morning');
    const [studentStats, setStudentStats] = useState({ totalDays: 0, daysPresent: 0, daysAbsent: 0, attendanceRate: 0 });
    const [statsLoading, setStatsLoading] = useState(false);

    const fetchAttendance = async (period = 'morning') => {
        setLoading(true);
        try {
            const { data } = await userRequest.get(`/attendance/student/${studentId}?class_id=${classId}&school_id=${school?._id}&term=${schoolTerm}&session=${schoolSession}&limit=50`);
            setStudentAttendance(data?.data || []);
        } catch (error) {
            console.error("Error fetching attendance:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchStudentAttendanceStats = async (period) => {
        if (!school?._id || !studentId) return;
        setStatsLoading(true);
        try {
            const { data } = await userRequest.get(
                `/attendance/stats?child_id=${studentId}&school_id=${school._id}&term=${schoolTerm}&session=${schoolSession}&period=${period}`
            );
            setStudentStats({
                totalDays: data.total_days ?? 0,
                daysPresent: data.total_present_days ?? 0,
                daysAbsent: data.total_absent_days ?? 0,
                attendanceRate: data.total_attendance_rate ?? 0,
            });
        } catch (error) {
            console.error("Error fetching student attendance stats:", error);
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        let isMounted = true;
        if (isMounted) {
            fetchAttendance(selectedPeriod);
            fetchStudentAttendanceStats(selectedPeriod);
        }
        return () => { isMounted = false; };
    }, [school?._id, user?._id]);

    useEffect(() => {
        if (school?._id && studentId) {
            fetchStudentAttendanceStats(selectedPeriod);
        }
    }, [selectedPeriod]);

    const periodDays = (record) => record.days.filter(d => (d.period || 'morning') === selectedPeriod);

    const findDay = (row, dayOfWeek) =>
        row.days.find(d => d.day_of_week === dayOfWeek && (d.period || 'morning') === selectedPeriod);

    const dayIcon = (day) => {
        if (!day) return <img src={Icons.studentPending} alt="Pending" />;
        if (day.status === 1) return <img src={Icons.studentPresent} alt="Present" />;
        return (
            <div className="relative inline-flex items-center justify-center" title={day.reason || 'Absent'}>
                <img src={Icons.studentAbsent} alt="Absent" />
                {day.reason && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 bg-orange-400 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold" style={{ fontSize: '7px' }}>!</span>
                    </span>
                )}
            </div>
        );
    };

    const dayNames = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'];

    const getAbsentWithReasons = (row) =>
        periodDays(row)
            .filter(d => d.status === 2 && d.reason)
            .sort((a, b) => a.day_of_week - b.day_of_week);

    const ExpandedReasons = ({ data: row }) => {
        const absent = getAbsentWithReasons(row);
        return (
            <div className="px-6 py-4 bg-red-50 border-t border-red-100">
                <p className="text-xs font-semibold text-red-500 uppercase tracking-wide mb-3">
                    Absence reasons — Week {row.week}
                </p>
                <div className="flex flex-wrap gap-2">
                    {absent.map(d => (
                        <div key={d._id || d.day_of_week} className="flex items-center gap-2 bg-white border border-red-200 rounded-xl px-3 py-2 shadow-sm">
                            <span className="text-xs font-bold text-red-500">{dayNames[d.day_of_week - 1]}</span>
                            <span className="text-gray-300 text-xs">|</span>
                            <span className="text-xs text-gray-700">{d.reason}</span>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const columns = [
        {
            name: "Week",
            selector: row => row.week,
        },
        {
            name: "Date Range",
            selector: row => {
                const days = row.days.filter(d => (d.period || 'morning') === selectedPeriod).map(d => new Date(d.date));
                const sorted = days.sort((a, b) => a - b);
                return sorted.length > 0
                    ? `${sorted[0].toLocaleDateString('en-NG')} - ${sorted[sorted.length - 1].toLocaleDateString('en-NG')}`
                    : '—';
            },
        },
        { name: "Monday",    cell: row => dayIcon(findDay(row, 1)) },
        { name: "Tuesday",   cell: row => dayIcon(findDay(row, 2)) },
        { name: "Wednesday", cell: row => dayIcon(findDay(row, 3)) },
        { name: "Thursday",  cell: row => dayIcon(findDay(row, 4)) },
        { name: "Friday",    cell: row => dayIcon(findDay(row, 5)) },
        {
            name: "Attendance Rate",
            cell: row => {
                const pd = periodDays(row);
                const rate = pd.length > 0 ? Math.round((pd.filter(d => d.status === 1).length / pd.length) * 100) : 0;
                return <p className="text-[#3DCE3D] font-semibold">{rate}%</p>;
            },
        },
    ];
    
    return (
        <div className='w-full flex items-center justify-center pt-24 pb-14 md:py-8 relative'>
            <div className='bg-transparent w-full pb-16 px-3 md:px-9 py-8 sm:py-0'>
                <div className='bg-white rounded-2xl p-6'>
                    <BackButton history={history} />
                    <div className='w-14 h-14 rounded-full bg-blue-500 mb-4 mt-4 border-[3px] border-[#D8846E]'></div>

                    <div className='flex flex-wrap items-center justify-between'>
                        <div className="w-full sm:w-max mb-4 sm:mb-0">
                            <p className="text-2xl font-semibold mb-2">{student?.firstname} {student?.lastname}</p>
                            <p className="text-sm text-[#606060] font-medium">
                                {schoolTerm} Term - {schoolSession} Attendance
                            </p>
                        </div>
                    </div>

                    {/* Period Toggle */}
                    <div className="flex justify-center mt-4">
                        <div className="flex bg-gray-100 rounded-2xl p-1 gap-1">
                            <button
                                onClick={() => setSelectedPeriod('morning')}
                                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                                    selectedPeriod === 'morning'
                                        ? 'bg-amber-500 text-white shadow-md'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <MdWbSunny className={selectedPeriod === 'morning' ? 'text-white' : 'text-amber-400'} />
                                Morning
                            </button>
                            <button
                                onClick={() => setSelectedPeriod('afternoon')}
                                className={`flex items-center gap-2 px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ${
                                    selectedPeriod === 'afternoon'
                                        ? 'bg-blue-600 text-white shadow-md'
                                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                                }`}
                            >
                                <MdCloud className={selectedPeriod === 'afternoon' ? 'text-white' : 'text-blue-400'} />
                                Afternoon
                            </button>
                        </div>
                    </div>

                    <div className='border-t border-t-[#E7E7E7] mt-6 mb-6'></div>

                    <div className='bg-[#EEEEFC] rounded-xl flex flex-wrap items-center justify-between'>
                        <div className='p-4'>
                            <p className='text-sm text-[#606060] mb-2'>Total Days</p>
                            <p className='text-3xl font-semibold'>{statsLoading ? '—' : studentStats.totalDays}</p>
                        </div>
                        <div className='p-4'>
                            <p className='text-sm text-[#606060] mb-2'>Days Present</p>
                            <p className='text-3xl font-semibold'>{statsLoading ? '—' : studentStats.daysPresent}</p>
                        </div>
                        <div className='p-4'>
                            <p className='text-sm text-[#606060] mb-2'>Days Absent</p>
                            <p className='text-3xl font-semibold'>{statsLoading ? '—' : studentStats.daysAbsent}</p>
                        </div>
                        <div className='p-4'>
                            <p className='text-sm text-[#606060] mb-2'>Attendance Rate</p>
                            <p className='text-3xl font-semibold'>{statsLoading ? '—' : `${studentStats.attendanceRate}%`}</p>
                        </div>
                    </div>
                </div>

                <div className='bg-white rounded-2xl mt-6 px-6 py-7'>
                    <p className='text-2xl font-semibold mb-3'>Attendance</p>
                    <div className="border rounded-3xl">
                        <DataTable
                            columns={columns}
                            data={studentAttendance}
                            expandableRows
                            expandableRowsComponent={ExpandedReasons}
                            expandableRowDisabled={row => getAbsentWithReasons(row).length === 0}
                            pagination
                            paginationComponent={customPagination}
                            paginationRowsPerPageOptions={[10, 20, 30, 50]}
                            customStyles={customStyles}
                            progressPending={loading}
                            progressComponent={<SkeletonTable rows={5} columns={6} />}
                            highlightOnHover
                            striped
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ViewStudentAttendance;
